import { createContext, useState } from 'react';

import {
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  Box,
  Flex,
  Text,
} from '@frontend/components';

import DisplayOptions from './DisplayOptions';

export type Cell = {
  row: number;
  column: number;
  element: HTMLDivElement;
};

export type BaseTableContextValue = {
  revertable: boolean;
  shouldHighlightOverrides: boolean;
  forceDefaultWhenBlank: boolean;
};

export type BuiltContextValue<T extends BaseTableContextValue> = T & {
  data: Record<string, Cell>;
  handleKeyDown: ({
    row,
    column,
    event,
  }: {
    row: number;
    column: number;
    event: React.KeyboardEvent<HTMLDivElement>;
  }) => boolean;
  setCellData: (cell: Cell) => void;
};

const buildContextValue = <T extends BaseTableContextValue>(
  context: T,
): BuiltContextValue<T> => {
  const data: Record<string, Cell> = {};

  return {
    ...context,
    data,
    handleKeyDown: ({
      row,
      column,
      event,
    }: {
      row: number;
      column: number;
      event: React.KeyboardEvent<HTMLDivElement>;
    }) => {
      if (event.shiftKey || event.key === 'Tab') {
        return true;
      }

      const grid: Cell[][] = [];
      Object.keys(data)
        .sort()
        .forEach((key) => {
          const cell = data[key];
          if (!grid[cell.row]) {
            grid[cell.row] = [];
          }

          grid[cell.row][cell.column] = cell;
        });
      const cells = grid.flat();
      const currentIndex = cells.findIndex(
        (cell) => cell.row === row && cell.column === column,
      );
      if (event.key === 'ArrowUp') {
        const rowsAbove = grid.filter((_, i) => i < row).reverse();

        const cellDirectlyAbove = rowsAbove
          .find((row) => row.find((cell) => cell?.column === column))
          ?.find((cell) => cell?.column === column);

        if (cellDirectlyAbove) {
          cellDirectlyAbove.element.focus();
        }
        // else {
        //   const nextBestCell = rowsAbove[0]?.find(
        //     (cell) => cell?.column >= column,
        //   );
        //   nextBestCell?.element?.focus();
        // }

        event.preventDefault();
        return true;
      } else if (event.key === 'ArrowDown') {
        const rowsBelow = grid.filter((_, i) => i > row);

        const cellDirectlyBelow = rowsBelow
          .find((row) => row.find((cell) => cell?.column === column))
          ?.find((cell) => cell?.column === column);

        if (cellDirectlyBelow) {
          cellDirectlyBelow.element.focus();
        }
        // else {
        //   const nextBestCell = rowsBelow[0]?.findLast(
        //     (cell) => cell?.column <= column,
        //   );
        //   nextBestCell?.element?.focus();
        // }

        event.preventDefault();
        return true;
      } else if (event.key === 'ArrowLeft') {
        cells
          .filter((_, i) => i < currentIndex)
          .reverse()[0]
          ?.element?.focus();
        event.preventDefault();
        return true;
      } else if (event.key === 'ArrowRight') {
        cells.find((_, i) => i > currentIndex)?.element?.focus();
        event.preventDefault();
        return true;
      }
      return false;
    },
    setCellData: (cell: Cell) => {
      data[`${cell.row}-${cell.column}`] = cell;
    },
  };
};

export const DEFAULT_CONTEXT = {
  revertable: true,
  shouldHighlightOverrides: true,
  forceDefaultWhenBlank: true,
};

export const TableContext = createContext(DEFAULT_CONTEXT);

export const AccordionTable = ({
  title,
  renderHeader,
  options,
  disabledOptions,
  cellTypeOptions,
  disableOptions,
  additionalOptions,
  onOptionsChange,
  context,
  children,
  accordionRef,
  boxProps,
  buttonProps,
  ...rest
}) => {
  const [isAccordionOpen, setIsAccordionOpen] = useState(true);
  const value = buildContextValue({
    ...DEFAULT_CONTEXT,
    ...context,
    isAccordionOpen,
    setIsAccordionOpen,
  });

  return (
    <TableContext.Provider value={value}>
      <Accordion
        mb={2}
        allowMultiple={false}
        index={isAccordionOpen ? 0 : -1}
        {...rest}
      >
        <AccordionItem
          overflow="clip"
          m={0}
          borderRadius="lg"
          ref={accordionRef}
        >
          <AccordionButton
            zIndex={3}
            display="flex"
            alignItems="center"
            borderTopRadius={0}
            onClick={() => setIsAccordionOpen(!isAccordionOpen)}
            _focusVisible={{
              boxShadow: 'focusInset',
            }}
            {...buttonProps}
          >
            <Box flex={1} textAlign="left">
              {renderHeader ? renderHeader(value) : title}
            </Box>
            {!disableOptions && (
              <DisplayOptions
                disabled={!isAccordionOpen}
                options={options}
                disabledOptions={disabledOptions}
                cellTypeOptions={cellTypeOptions}
                additionalOptions={additionalOptions}
                onOptionsChange={onOptionsChange}
              />
            )}
          </AccordionButton>
          <AccordionPanel
            p={0}
            border="none"
            borderBottomRadius="lg"
            overflow="hidden"
          >
            <Box role="table" overflowX="auto" {...boxProps}>
              {children}
            </Box>
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
    </TableContext.Provider>
  );
};

export const Table = ({
  title,
  renderHeader,
  options,
  cellTypeOptions,
  disableOptions,
  disableHeader,
  disabledOptions,
  additionalOptions,
  onOptionsChange,
  context,
  children,
  containerRef,
  boxProps,
  containerProps,
}) => {
  const value = buildContextValue({
    ...DEFAULT_CONTEXT,
    ...context,
  });

  return (
    <TableContext.Provider value={value}>
      <Box
        mb={2}
        ref={containerRef}
        borderRadius="lg"
        border="1px"
        borderColor="separator.border"
        width="100%"
        overflow="hidden"
        {...containerProps}
      >
        {!disableHeader && (
          <Flex py={1} px={1.5}>
            <Box flex={1} textAlign="left">
              {renderHeader ? (
                renderHeader(value)
              ) : (
                <Text as="span" textTransform="capitalize" fontWeight="heavy">
                  {title}
                </Text>
              )}
            </Box>
            {!disableOptions && (
              <DisplayOptions
                options={options}
                disabledOptions={disabledOptions}
                cellTypeOptions={cellTypeOptions}
                additionalOptions={additionalOptions}
                onOptionsChange={onOptionsChange}
              />
            )}
          </Flex>
        )}
        <Box role="table" overflowX="auto" {...boxProps}>
          {children}
        </Box>
      </Box>
    </TableContext.Provider>
  );
};
